/* Minimal CommonMark subset renderer for Unit content.
 *
 * Renders to REACT ELEMENTS, never `dangerouslySetInnerHTML`. That is the point:
 * Unit markdown is Factory-authored and release-validated, but 03 §5.5 still
 * treats all content as data. With no HTML sink there is no XSS surface to
 * reason about — an injected `<script>` in a lesson body renders as the literal
 * text a learner would see in a code block, which is exactly right.
 *
 * Covers what the exemplar Units actually use: fenced code, tables, headings,
 * lists, blockquotes, paragraphs, plus inline bold/italic/code/links.
 */

import type { ReactNode } from 'react';

let keySeq = 0;
const nextKey = () => `md-${keySeq++}`;

/** Inline: `code`, **bold**, *italic*, [text](href). Code wins over emphasis. */
function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Split on inline code first so ** inside backticks stays literal.
  const parts = text.split(/(`[^`]+`)/g);

  for (const part of parts) {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      out.push(<code key={nextKey()}>{part.slice(1, -1)}</code>);
      continue;
    }

    let rest = part;
    const pattern = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)\s]+\))/;
    let match = pattern.exec(rest);

    while (match) {
      if (match.index > 0) out.push(rest.slice(0, match.index));
      const token = match[0];

      if (token.startsWith('**')) {
        out.push(<strong key={nextKey()}>{token.slice(2, -2)}</strong>);
      } else if (token.startsWith('*')) {
        out.push(<em key={nextKey()}>{token.slice(1, -1)}</em>);
      } else {
        const linkMatch = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
        const label = linkMatch?.[1] ?? token;
        const href = linkMatch?.[2] ?? '';
        // Only http(s) and relative links. A javascript: or data: URL in content
        // renders as inert text rather than becoming a clickable sink.
        const safe = /^(https?:\/\/|\/)/.test(href);
        out.push(
          safe
            ? <a key={nextKey()} href={href} target="_blank" rel="noreferrer noopener">{label}</a>
            : <span key={nextKey()}>{label}</span>,
        );
      }

      rest = rest.slice(match.index + token.length);
      match = pattern.exec(rest);
    }
    if (rest) out.push(rest);
  }

  return out;
}

function renderTable(lines: string[]): ReactNode {
  const rows = lines.map((l) =>
    l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim()),
  );
  const header = rows[0] ?? [];
  const body = rows.slice(2); // row 1 is the |---|---| separator

  return (
    <div className="md-table-wrap" key={nextKey()}>
      <table className="md-table">
        <thead>
          <tr>{header.map((c) => <th key={nextKey()}>{renderInline(c)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r) => (
            <tr key={nextKey()}>{r.map((c) => <td key={nextKey()}>{renderInline(c)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Markdown({ source }: { source: string }): ReactNode {
  const lines = source.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Fenced code block.
    if (line.trimStart().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').trimStart().startsWith('```')) {
        buf.push(lines[i] ?? '');
        i++;
      }
      i++; // closing fence
      blocks.push(
        <pre className="md-pre" key={nextKey()} data-lang={lang || undefined}>
          <code>{buf.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Table: a pipe row followed by a separator row.
    if (line.includes('|') && /^\s*\|?[\s:-]*-[\s:|-]*\|?\s*$/.test(lines[i + 1] ?? '')) {
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? '').includes('|')) {
        buf.push(lines[i] ?? '');
        i++;
      }
      blocks.push(renderTable(buf));
      continue;
    }

    // ATX heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      const Tag = (`h${Math.min(level + 2, 6)}`) as 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={nextKey()} className="md-h">{renderInline(heading[2] ?? '')}</Tag>);
      i++;
      continue;
    }

    // Blockquote.
    if (line.startsWith('> ')) {
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('> ')) {
        buf.push((lines[i] ?? '').slice(2));
        i++;
      }
      blocks.push(
        <blockquote className="md-quote" key={nextKey()}>{renderInline(buf.join(' '))}</blockquote>,
      );
      continue;
    }

    // Lists (ordered or bulleted).
    const listItem = /^\s*(?:([-*+])|(\d+)\.)\s+(.*)$/.exec(line);
    if (listItem) {
      const ordered = listItem[2] !== undefined;
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^\s*(?:[-*+]|\d+\.)\s+(.*)$/.exec(lines[i] ?? '');
        if (!m) break;
        items.push(m[1] ?? '');
        i++;
      }
      blocks.push(
        ordered
          ? <ol className="md-list" key={nextKey()}>{items.map((t) => <li key={nextKey()}>{renderInline(t)}</li>)}</ol>
          : <ul className="md-list" key={nextKey()}>{items.map((t) => <li key={nextKey()}>{renderInline(t)}</li>)}</ul>,
      );
      continue;
    }

    // Blank line.
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph: consume until a blank line or a block starter.
    const buf: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      if (
        l.trim() === '' ||
        l.trimStart().startsWith('```') ||
        /^#{1,6}\s/.test(l) ||
        l.startsWith('> ') ||
        /^\s*(?:[-*+]|\d+\.)\s+/.test(l)
      ) break;
      buf.push(l);
      i++;
    }
    blocks.push(<p className="md-p" key={nextKey()}>{renderInline(buf.join(' '))}</p>);
  }

  return <>{blocks}</>;
}
